import { getConfig } from "./config";

/**
 * Host sign-in with Cognito managed login: authorization code + PKCE, no SDK (SPEC §5).
 * Tokens live in localStorage so a console survives reloads during a game.
 */
const STORE = "kalaporssi:host";
const PKCE = "kalaporssi:pkce";

interface Tokens {
  accessToken: string;
  refreshToken?: string;
  expiresAt: number;
  email?: string;
}

const redirectUri = () => `${window.location.origin}/host/callback`;

function read(): Tokens | null {
  try {
    const raw = localStorage.getItem(STORE);
    return raw ? (JSON.parse(raw) as Tokens) : null;
  } catch {
    return null;
  }
}

function write(t: Tokens | null) {
  try {
    if (t) localStorage.setItem(STORE, JSON.stringify(t));
    else localStorage.removeItem(STORE);
  } catch {
    // storage unavailable: session-only sign-in
  }
}

const b64url = (buf: ArrayBuffer | Uint8Array) =>
  btoa(String.fromCharCode(...new Uint8Array(buf)))
    .replace(/\+/g, "-")
    .replace(/\//g, "_")
    .replace(/=+$/, "");

function emailFromIdToken(idToken: string | undefined): string | undefined {
  try {
    const payload = JSON.parse(atob((idToken ?? "").split(".")[1]?.replace(/-/g, "+").replace(/_/g, "/") ?? ""));
    return typeof payload.email === "string" ? payload.email : undefined;
  } catch {
    return undefined;
  }
}

export async function startLogin(returnTo = "/host") {
  const { cognito } = getConfig();
  const verifier = b64url(crypto.getRandomValues(new Uint8Array(32)));
  const challenge = b64url(await crypto.subtle.digest("SHA-256", new TextEncoder().encode(verifier)));
  const state = b64url(crypto.getRandomValues(new Uint8Array(16)));
  sessionStorage.setItem(PKCE, JSON.stringify({ verifier, state, returnTo }));
  const q = new URLSearchParams({
    response_type: "code",
    client_id: cognito.clientId,
    redirect_uri: redirectUri(),
    scope: "openid email profile",
    code_challenge_method: "S256",
    code_challenge: challenge,
    state,
  });
  window.location.assign(`${cognito.domain}/oauth2/authorize?${q}`);
}

async function tokenRequest(body: Record<string, string>) {
  const { cognito } = getConfig();
  const res = await fetch(`${cognito.domain}/oauth2/token`, {
    method: "POST",
    headers: { "content-type": "application/x-www-form-urlencoded" },
    body: new URLSearchParams({ client_id: cognito.clientId, ...body }),
  });
  if (!res.ok) throw new Error(`token endpoint: HTTP ${res.status}`);
  return (await res.json()) as { access_token: string; id_token?: string; refresh_token?: string; expires_in: number };
}

/** Completes the redirect from Cognito. Returns the path to continue to. */
export async function completeLogin(search: string): Promise<string> {
  const params = new URLSearchParams(search);
  const saved = JSON.parse(sessionStorage.getItem(PKCE) ?? "null") as {
    verifier: string;
    state: string;
    returnTo: string;
  } | null;
  sessionStorage.removeItem(PKCE);
  const code = params.get("code");
  if (!saved || !code || params.get("state") !== saved.state) throw new Error("Kirjautuminen epäonnistui.");
  const t = await tokenRequest({
    grant_type: "authorization_code",
    code,
    redirect_uri: redirectUri(),
    code_verifier: saved.verifier,
  });
  write({
    accessToken: t.access_token,
    refreshToken: t.refresh_token,
    expiresAt: Date.now() + t.expires_in * 1000,
    email: emailFromIdToken(t.id_token),
  });
  return saved.returnTo.startsWith("/") ? saved.returnTo : "/host";
}

/** Valid access token, refreshed when close to expiry; null when signed out. */
export async function getHostToken(): Promise<string | null> {
  const t = read();
  if (!t) return null;
  if (t.expiresAt - Date.now() > 60_000) return t.accessToken;
  if (!t.refreshToken) {
    write(null);
    return null;
  }
  try {
    const r = await tokenRequest({ grant_type: "refresh_token", refresh_token: t.refreshToken });
    write({ ...t, accessToken: r.access_token, expiresAt: Date.now() + r.expires_in * 1000 });
    return r.access_token;
  } catch {
    write(null);
    return null;
  }
}

export const isSignedIn = () => read() !== null;
export const signedInEmail = () => read()?.email;

export function logout() {
  write(null);
  const { cognito } = getConfig();
  const q = new URLSearchParams({ client_id: cognito.clientId, logout_uri: `${window.location.origin}/` });
  window.location.assign(`${cognito.domain}/logout?${q}`);
}
